//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { type RawGraphSchema } from '@/services/apiTypes';

// The wire contract with the backend's agent runtime.  These types mirror
// the Pydantic models in desktop_backend/app/agents/contract.py field for
// field; the fixtures under __fixtures__, one directory per contract
// version, are generated there and copied here, and contract.test.ts holds
// the two in step.  See docs/design/agent-runtime.md, "Contract".

export const AGENT_CONTRACT_VERSION = 2;

export type AgentPage =
  'graphs' | 'dashboard' | 'datasets' | 'explorer' | 'jobs' | 'other';

// A source as the Graphs page describes it, under the same names as an
// infer_schema request row so one builder feeds both.  `part` is the
// schema-inference sample: it goes to the backend for inspection and to
// nothing else.
export interface SnapshotSource {
  id: string;
  name: string;
  kind: 'csv' | 'parquet' | 'link' | 'sql';
  part?: string;
  size?: number;
  inferredDelimiter?: string;
  headerMode?: string;
  query?: string;
  connection?: string;
}

export interface LoadResultSummary {
  source_id: string;
  frame: string;
  rows_loaded: number;
  // The load job, which is where the backend reads rejected-row counts and
  // error text; the page knows only the terminal message.
  job_id: number | null;
  error: string | null;
}

// What a page publishes: its own content, without the dataset.  The
// dataset is the header's selection, not the page's to report; the
// transport adds it at send time.
export interface GraphsPublication {
  page: 'graphs';
  sources: SnapshotSource[];
  schema: RawGraphSchema | null;
  load_results: LoadResultSummary[] | null;
}

export interface BarePagePublication {
  page: Exclude<AgentPage, 'graphs'>;
}

export type PagePublication = GraphsPublication | BarePagePublication;

// The header's dataset, or null when none is choosable.
export interface SnapshotDataset {
  active_dataset: string | null;
}

export type GraphsSnapshot = GraphsPublication & SnapshotDataset;
export type BarePageSnapshot = BarePagePublication & SnapshotDataset;
export type PageSnapshot = GraphsSnapshot | BarePageSnapshot;

export type UiAction =
  | { page: 'graphs'; kind: 'show_draft'; version: number }
  | { page: 'graphs'; kind: 'start_load'; graph: string }
  | { page: '*'; kind: 'navigate';
      to: Exclude<AgentPage, 'other'> }
  | { page: 'jobs'; kind: 'show_job'; job_id: number };

export interface PendingApproval {
  id: string;
  tool: string;
  summary: string;
  expires_at: string;
}

export type TurnResult =
  | { kind: 'reply'; message: string; ui_actions: UiAction[];
      draft_version: number | null }
  | { kind: 'awaiting_approval'; message: string;
      approval: PendingApproval }
  | { kind: 'error'; error: string };

export interface TraceEntry {
  step: number;
  kind: 'thought' | 'tool' | 'result';
  summary: string;
}

// What async_status returns with HTTP 202 while an agent turn runs.
export interface AgentProgress {
  status: 'in_progress';
  execution_trace: TraceEntry[];
  step_count: number;
  message?: string;
  // The reply text streamed so far; absent before any arrives.
  partial_text?: string;
}

export type ApprovalDecision = 'approved' | 'denied';

export interface TranscriptApproval extends PendingApproval {
  decision: ApprovalDecision | 'expired' | null;
  decided_at: string | null;
}

export interface TranscriptMessage {
  role: 'user' | 'assistant';
  content: string;
  ui_actions?: UiAction[];
  approval?: TranscriptApproval;
  // A turn the user stopped; the entry carries the steps it had taken and
  // the reply text a cancelled stream had produced.
  stopped?: boolean;
  trace?: TraceEntry[];
  partial?: string;
  // The dataset the turn carried, on the entry that ended it; null when
  // there was none, absent on an entry that records no turn.
  snapshot_dataset?: string | null;
}

export interface DraftVersion {
  version: number;
  schema: RawGraphSchema;
  source: 'agent' | 'user';
  at: string;
}

export interface ConversationView {
  contract_version: number;
  status: 'idle' | 'running' | 'awaiting_approval';
  messages: TranscriptMessage[];
  drafts: DraftVersion[];
  pending_approval: PendingApproval | null;
}

// ---------------------------------------------------------------------------
// Guards.  Wire data is `unknown` until one of these has looked at it.
// ---------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const PAGES: readonly AgentPage[] =
  ['graphs', 'dashboard', 'datasets', 'explorer', 'jobs', 'other'];

const isAgentPage = (value: unknown): value is AgentPage =>
  typeof value === 'string' && (PAGES as readonly string[]).includes(value);

export const isPageSnapshot = (value: unknown): value is PageSnapshot => {
  if (!isRecord(value) || !isAgentPage(value.page) ||
      (value.active_dataset !== null &&
       typeof value.active_dataset !== 'string'))
    return false;
  if (value.page !== 'graphs') return true;
  return Array.isArray(value.sources) && 'schema' in value &&
         'load_results' in value;
};

export const isUiAction = (value: unknown): value is UiAction => {
  if (!isRecord(value) || typeof value.kind !== 'string') return false;
  switch (value.kind) {
    case 'show_draft':
      return value.page === 'graphs' && typeof value.version === 'number';
    case 'start_load':
      return value.page === 'graphs' && typeof value.graph === 'string';
    case 'navigate':
      return value.page === '*' && isAgentPage(value.to) &&
             value.to !== 'other';
    case 'show_job':
      return value.page === 'jobs' && typeof value.job_id === 'number';
    default:
      return false;
  }
};

const isPendingApproval = (value: unknown): value is PendingApproval =>
  isRecord(value) && typeof value.id === 'string' &&
  typeof value.tool === 'string' && typeof value.summary === 'string' &&
  typeof value.expires_at === 'string';

export const isTurnResult = (value: unknown): value is TurnResult => {
  if (!isRecord(value)) return false;
  switch (value.kind) {
    case 'reply':
      return typeof value.message === 'string' &&
             Array.isArray(value.ui_actions) &&
             value.ui_actions.every(isUiAction) &&
             (value.draft_version === null ||
              typeof value.draft_version === 'number');
    case 'awaiting_approval':
      return typeof value.message === 'string' &&
             isPendingApproval(value.approval);
    case 'error':
      return typeof value.error === 'string';
    default:
      return false;
  }
};

const isTraceEntry = (value: unknown): value is TraceEntry =>
  isRecord(value) && typeof value.step === 'number' &&
  (value.kind === 'thought' || value.kind === 'tool' ||
   value.kind === 'result') && typeof value.summary === 'string';

const isOptionalString = (value: unknown): boolean =>
  value === undefined || typeof value === 'string';

export const isAgentProgress = (value: unknown): value is AgentProgress =>
  isRecord(value) && value.status === 'in_progress' &&
  typeof value.step_count === 'number' &&
  Array.isArray(value.execution_trace) &&
  value.execution_trace.every(isTraceEntry) &&
  isOptionalString(value.partial_text);

const isTranscriptApproval =
  (value: unknown): value is TranscriptApproval =>
    isRecord(value) && isPendingApproval(value) &&
    (value.decision === null || value.decision === 'approved' ||
     value.decision === 'denied' || value.decision === 'expired') &&
    (value.decided_at === null || typeof value.decided_at === 'string');

// The backend leaves an absent optional field out rather than sending null,
// so undefined is the only absence accepted here.
const isTranscriptMessage =
  (value: unknown): value is TranscriptMessage =>
    isRecord(value) &&
    (value.role === 'user' || value.role === 'assistant') &&
    typeof value.content === 'string' &&
    (value.ui_actions === undefined ||
     (Array.isArray(value.ui_actions) &&
      value.ui_actions.every(isUiAction))) &&
    (value.approval === undefined || isTranscriptApproval(value.approval)) &&
    (value.stopped === undefined || typeof value.stopped === 'boolean') &&
    (value.trace === undefined ||
     (Array.isArray(value.trace) && value.trace.every(isTraceEntry))) &&
    isOptionalString(value.partial) &&
    (value.snapshot_dataset === undefined ||
     value.snapshot_dataset === null ||
     typeof value.snapshot_dataset === 'string');

// The schema is checked only for being an object: it is the editor's own
// shape, validated where the editor takes it in.
const isDraftVersion = (value: unknown): value is DraftVersion =>
  isRecord(value) && typeof value.version === 'number' &&
  isRecord(value.schema) &&
  (value.source === 'agent' || value.source === 'user') &&
  typeof value.at === 'string';

// Every element is checked, not just the arrays: the panel and the Graphs
// page dereference messages and drafts, so drift in one has to fail here
// rather than as a TypeError in a component.
export const isConversationView =
  (value: unknown): value is ConversationView =>
    isRecord(value) && typeof value.contract_version === 'number' &&
    (value.status === 'idle' || value.status === 'running' ||
     value.status === 'awaiting_approval') &&
    Array.isArray(value.messages) &&
    value.messages.every(isTranscriptMessage) &&
    Array.isArray(value.drafts) && value.drafts.every(isDraftVersion) &&
    (value.pending_approval === null ||
     isPendingApproval(value.pending_approval));
